/** Типы бустеров = сегмент пути API `/game/booster/{type}/redeem`. */
export type BoosterRedeemType =
  | "coins"
  | "chakra"
  | "energy"
  | "shard-dust"
  | "style-token"
  | "fuse"

export type RedeemKind = "card" | BoosterRedeemType

export type ParsedRedeemPayload = {
  kind: RedeemKind
  code: string
}

/** Как у текущего скана физических карт. */
export const CARD_CODE_RE = /^CARD-[A-Z]+-[A-Z]+-[A-Z0-9]{6}$/

const BOOSTER_PREFIXES: ReadonlyArray<{
  prefix: string
  kind: BoosterRedeemType
}> = [
  { prefix: "BOOSTER-COINS-", kind: "coins" },
  { prefix: "BOOSTER-CHAKRA-", kind: "chakra" },
  { prefix: "BOOSTER-ENERGY-", kind: "energy" },
  { prefix: "BOOSTER-SHARD-DUST-", kind: "shard-dust" },
  { prefix: "BOOSTER-STYLE-TOKEN-", kind: "style-token" },
  { prefix: "BOOSTER-FUSE-", kind: "fuse" },
]

const BOOSTER_PATH_RE =
  /\/game\/booster\/(coins|chakra|energy|shard-dust|style-token|fuse)\/redeem$/i

function extractCodeParam(search: string): string | null {
  const q = search.startsWith("?") ? search.slice(1) : search
  if (!q) return null
  try {
    const value = new URLSearchParams(q).get("code")
    return value?.trim() ? value.trim() : null
  } catch {
    const match = /(?:^|[&?])code=([^&]+)/i.exec(
      search.includes("=") ? search : `?${search}`
    )
    if (!match?.[1]) return null
    try {
      return decodeURIComponent(match[1]).trim() || null
    } catch {
      return match[1].trim() || null
    }
  }
}

function splitPathAndSearch(raw: string): { path: string; search: string } | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed)
      return { path: url.pathname, search: url.search }
    }
  } catch {
    return null
  }

  if (trimmed.startsWith("/") || trimmed.toLowerCase().includes("/game/")) {
    const q = trimmed.indexOf("?")
    if (q >= 0) {
      return { path: trimmed.slice(0, q), search: trimmed.slice(q) }
    }
    return { path: trimmed, search: "" }
  }

  return null
}

export function classifyRedeemCode(rawCode: string): ParsedRedeemPayload | null {
  const code = rawCode.trim().toUpperCase()
  if (!code) return null

  if (CARD_CODE_RE.test(code)) {
    return { kind: "card", code }
  }

  for (const { prefix, kind } of BOOSTER_PREFIXES) {
    if (code.startsWith(prefix) && code.length > prefix.length) {
      return { kind, code }
    }
  }

  return null
}

/**
 * Разбор QR: голый код (`CARD-…` / `BOOSTER-COINS-…`) или URL/path
 * вида `/game/booster/coins/redeem?code=…`.
 */
export function parseRedeemPayload(raw: string): ParsedRedeemPayload | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const parts = splitPathAndSearch(trimmed)
  if (parts) {
    const pathNorm = parts.path.replace(/\/+$/, "")
    const boosterMatch = BOOSTER_PATH_RE.exec(pathNorm)
    if (boosterMatch?.[1]) {
      const kind = boosterMatch[1].toLowerCase() as BoosterRedeemType
      const codeParam = extractCodeParam(parts.search)
      if (codeParam) {
        return { kind, code: codeParam.trim().toUpperCase() }
      }
    }

    if (/\/game\/cards\/redeem$/i.test(pathNorm)) {
      const codeParam = extractCodeParam(parts.search)
      if (codeParam) {
        const classified = classifyRedeemCode(codeParam)
        if (classified) return classified
        const code = codeParam.trim().toUpperCase()
        if (code.startsWith("CARD-")) return { kind: "card", code }
      }
    }

    const codeFromQuery = extractCodeParam(parts.search)
    if (codeFromQuery) {
      const classified = classifyRedeemCode(codeFromQuery)
      if (classified) return classified
    }
  }

  if (/[?&]code=/i.test(trimmed)) {
    const qIndex = trimmed.indexOf("?")
    const search =
      qIndex >= 0 ? trimmed.slice(qIndex) : trimmed.includes("code=")
        ? `?${trimmed}`
        : ""
    const codeParam = extractCodeParam(search)
    if (codeParam) {
      const classified = classifyRedeemCode(codeParam)
      if (classified) return classified
    }
  }

  return classifyRedeemCode(trimmed)
}

export function isBoosterRedeemKind(
  kind: RedeemKind
): kind is BoosterRedeemType {
  return kind !== "card"
}
